import React from "react";
import { captureReferralFromSearch } from "../utils/referrals";
import { trackEvent } from "../analytics/track";

// ─── Helpers ──────────────────────────────────────────────────────────────────

// The public calculator uses completed windows and scheduled-time DCA.
// Use the complete historical run, never the most favorable subperiod.
function getCalculatorStats(data) {
  const totalExtraSats = Number(data?.extra_sats_total);
  const extraPct = Number(data?.extra_pct);
  const windows = Array.isArray(data?.windows) ? data.windows : [];

  if (
    !windows.length ||
    !Number.isFinite(totalExtraSats) ||
    !Number.isFinite(extraPct)
  ) {
    throw new Error("Not enough completed windows for a historical comparison.");
  }

  return {
    totalExtraSats,
    extraPct,
    spanDays: Number(data?.period_days) || 365,
    numWindows: Number(data?.num_windows) || windows.length,
  };
}

function signedSats(value) {
  const n = Math.round(Number(value) || 0);
  return `${n > 0 ? "+" : ""}${n.toLocaleString()}`;
}

function signedPct(value) {
  const n = Number(value) || 0;
  return `${n > 0 ? "+" : ""}${n.toFixed(2)}%`;
}

const APP_STORE_URL  = "https://apps.apple.com/app/id6748700654";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.satstacker.app";

function trackStoreClick(store, cb) {
  trackEvent("app_store_click", {
    store,
    page: "download",
  });

  cb?.();
}

// ─── Download CTAs ────────────────────────────────────────────────────────────

function DownloadButtons({ size = "lg", label = null }) {
  const isIOS     = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);
  const py        = size === "lg" ? "py-4" : "py-3";
  const text      = size === "lg" ? "text-base" : "text-sm";

  if (isIOS || isAndroid) {
    const store = isIOS ? "ios" : "android";
    const url   = isIOS ? APP_STORE_URL : PLAY_STORE_URL;
    return (
      <button
        onClick={() => trackStoreClick(store, () => (window.location.href = url))}
        className={`w-full ${py} px-6 rounded-xl bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-bold ${text} transition-colors`}
      >
        {label || `Download for ${isIOS ? "iOS" : "Android"}`}
      </button>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row gap-3 w-full">
      <a
        href={APP_STORE_URL}
        onClick={(e) => { e.preventDefault(); trackStoreClick("ios", () => window.open(APP_STORE_URL, "_blank", "noreferrer")); }}
        className={`flex-1 ${py} px-6 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold ${text} text-center transition-colors`}
      >
        App Store
      </a>
      <a
        href={PLAY_STORE_URL}
        onClick={(e) => { e.preventDefault(); trackStoreClick("android", () => window.open(PLAY_STORE_URL, "_blank", "noreferrer")); }}
        className={`flex-1 ${py} px-6 rounded-xl border border-zinc-700 hover:border-zinc-500 hover:bg-zinc-800 text-white font-bold ${text} text-center transition-colors`}
      >
        Google Play
      </a>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Download() {
  const resultsRef = React.useRef(null);
  const referralCode = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("ref");
  }, []);
  const isReferralVisit = !!referralCode;

  const [cadence, setCadence] = React.useState("weekly");
  const [buyUsd, setBuyUsd] = React.useState(25);
  const [lastResult, setLastResult] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const monthlyBudget = React.useMemo(() => {
    const amount = Number(buyUsd) || 0;
    if (cadence === "daily") return amount * 30;
    if (cadence === "weekly") return amount * 4;
    return amount * 2;
  }, [cadence, buyUsd]);

  React.useEffect(() => {
    captureReferralFromSearch(window.location.search);
  }, []);

  const onSeeResults = async (e) => {
    e.preventDefault();
    if (loading) return;

    const amount = Number(buyUsd);
    if (!Number.isFinite(amount) || amount < 5 || amount > 100000) {
      setError("Enter a buy amount between $5 and $100,000.");
      return;
    }

    trackEvent("marketing_chart_submit", {
      page: "download",
      cadence,
      buy_usd: amount,
      monthly_budget_usd: monthlyBudget,
    });

    setLoading(true);
    setError("");
    setLastResult(null);

    try {
      const base = import.meta.env.VITE_API_BASE_URL || "";
      const url =
        `${base}/public/calculator?cadence=${encodeURIComponent(cadence)}` +
        `&buy_usd=${encodeURIComponent(amount)}` +
        `&strategy=production_candidate`;

      const res = await fetch(url);
      if (!res.ok) throw new Error(`Historical calculator unavailable (${res.status}).`);

      const data = await res.json();
      const stats = getCalculatorStats(data);
      const buyLabel =
        cadence === "daily"
          ? `$${amount.toLocaleString()}/day`
          : cadence === "weekly"
            ? `$${amount.toLocaleString()}/week`
            : `$${amount.toLocaleString()}/2wk`;

      setLastResult({ ...stats, buyLabel });
      requestAnimationFrame(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    } catch (err) {
      console.error("Failed to load historical calculator", err);
      setError(err?.message || "Could not load historical results. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white overflow-x-hidden">
      {/* Hero */}
      <div className="px-4 pt-8 pb-8 sm:pt-24 sm:pb-16 max-w-lg mx-auto text-center">
        <div className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-full px-3 py-1.5 mb-6">
          <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse flex-shrink-0" />
          <span className="text-xs font-medium text-zinc-400 tracking-wide">
            {isReferralVisit
              ? "🎁 Referral offer · 2 months free"
              : "Smart Timing · Automatic DCA"}
          </span>
        </div>

        <h1 className="text-3xl sm:text-5xl font-bold tracking-tight leading-tight mb-3">
          {isReferralVisit ? (
            <>
              A friend gave you<br />
              <span className="text-orange-400">an extra month free.</span>
            </>
          ) : (
            <>
              Stack more sats.<br />
              <span className="text-orange-400">Same budget.</span>
            </>
          )}
        </h1>

        <p className="text-zinc-400 text-sm sm:text-lg leading-relaxed mb-6 max-w-sm mx-auto">
          {isReferralVisit
            ? "Start Smart Timing with 2 months free instead of the usual 1 month. After that, it renews at the standard monthly price unless canceled."
            : "SatStacker looks for better entry opportunities within your recurring Bitcoin buy windows, without changing your budget or cadence."}
        </p>

        <DownloadButtons
          size="lg"
          label={isReferralVisit ? "Get SatStacker · 2 months free" : null}
        />

        {isReferralVisit && (
          <p className="mt-3 text-xs text-orange-400 font-semibold">
            Referral offer applies when you start Smart Timing.
          </p>
        )}
        <p className="mt-4 text-xs text-zinc-600">
          Non-custodial · Works with Strike · No leverage
        </p>
      </div>

      {/* One benchmark, one results section. The old second-endpoint graph is removed. */}
      <div className="px-4 pb-10 max-w-lg mx-auto">
        <div className="border-t border-zinc-800/60 mb-6" />
        <div className="text-center mb-5">
          <h2 className="text-lg font-bold mb-1">Try a historical Smart Timing simulation</h2>
          <p className="text-sm text-zinc-500">
            Enter your recurring buy and see how the modeled strategy compares with scheduled DCA.
          </p>
        </div>

        <form
          onSubmit={onSeeResults}
          className="bg-zinc-900 border border-zinc-800 rounded-2xl p-4 mb-4"
        >
          <div className="mb-4">
            <div className="text-xs text-zinc-500 mb-2">How often do you buy?</div>
            <div className="flex gap-2">
              {[
                { value: "daily", label: "Daily" },
                { value: "weekly", label: "Weekly" },
                { value: "bi-weekly", label: "Bi-weekly" },
              ].map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setCadence(value)}
                  className={`flex-1 py-2.5 rounded-lg text-xs font-semibold transition-colors border ${
                    cadence === value
                      ? "bg-orange-500 border-orange-500 text-white"
                      : "bg-transparent border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-white"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="download-buy-usd" className="block text-xs text-zinc-500 mb-2">
              How much per buy?
            </label>
            <div className="flex items-center bg-zinc-950 border border-zinc-700 rounded-xl px-4 py-3 focus-within:border-orange-500 transition-colors">
              <span className="text-zinc-500 text-lg mr-1">$</span>
              <input
                id="download-buy-usd"
                value={buyUsd}
                onChange={(e) => setBuyUsd(e.target.value)}
                inputMode="decimal"
                type="number"
                min="5"
                max="100000"
                step="0.01"
                required
                placeholder="25"
                className="flex-1 bg-transparent text-white text-xl font-semibold outline-none placeholder-zinc-700 min-w-0"
              />
              <span className="text-zinc-600 text-xs flex-shrink-0 ml-2">
                ≈ ${Math.round(monthlyBudget).toLocaleString()}/mo
              </span>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3.5 rounded-xl bg-orange-500 hover:bg-orange-600 active:bg-orange-700 disabled:opacity-60 text-white font-bold text-base transition-colors"
          >
            {loading ? "Running the simulation…" : "See historical results →"}
          </button>
        </form>

        <div ref={resultsRef} className="scroll-mt-6">
          {error && (
            <div role="alert" className="rounded-2xl border border-red-500/30 bg-red-950/20 p-4 mb-4 text-sm text-red-300">
              {error}
            </div>
          )}

          {lastResult && (
            <div className="bg-zinc-900/60 border border-orange-500/20 rounded-2xl p-5 mb-4 text-center">
              <p className="text-xs text-zinc-500 uppercase tracking-widest mb-2">
                Historical simulation · {lastResult.buyLabel}
              </p>
              <p className={`text-4xl font-bold mb-1 ${
                lastResult.totalExtraSats >= 0 ? "text-orange-400" : "text-zinc-200"
              }`}>
                {signedSats(lastResult.totalExtraSats)} sats
              </p>
              <p className="text-sm text-zinc-300">
                {signedPct(lastResult.extraPct)} vs scheduled DCA
              </p>
              <p className="mt-2 text-xs text-zinc-500">
                {lastResult.numWindows} completed windows across the last {lastResult.spanDays} days
              </p>
              <div className="mt-4 pt-3 border-t border-zinc-800 text-xs text-zinc-500 leading-relaxed">
                Uses historical prices and modeled Basic (0.75%) and Smart Timing
                (0.25%) app fees. Strike charges and the subscription cost are not
                included. This is a simulation, not a record of your actual buys;
                future results may differ.
              </div>
            </div>
          )}

          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5 text-center mb-5">
            <p className="text-sm font-semibold text-white mb-1">
              Want to explore the individual buy windows?
            </p>
            <p className="text-xs text-zinc-500 mb-4 leading-relaxed">
              The full calculator shows the historical breakdown using your own amount and cadence.
            </p>
            <a
              href="/calculator"
              onClick={() => trackEvent("calculator_full_open", { page: "download" })}
              className="inline-block rounded-xl border border-orange-500/30 bg-orange-500/10 px-5 py-3 text-sm font-semibold text-orange-400 hover:bg-orange-500/20 transition-colors"
            >
              Explore the full DCA calculator →
            </a>
          </div>
        </div>

        {lastResult && (
          <div className="text-center mb-5">
            <div className="border-t border-zinc-800/60 my-6" />
            <p className="text-lg font-bold mb-1">Ready to start stacking smarter?</p>
            <p className="text-sm text-zinc-500 mb-5">
              Connect Strike, choose your plan, and let Smart Timing do the work.
            </p>
            <DownloadButtons size="lg" label="Get SatStacker" />
          </div>
        )}

        <p className="text-center text-xs text-zinc-700 pb-4 mt-4">
          Non-custodial · Works with Strike · No leverage
        </p>
      </div>
    </div>
  );
}
